import PropTypes from 'prop-types';
import React from 'react';
import { useMediaQuery } from 'react-responsive';

import CartCount from 'components/CartCount';
import { TertiaryHeading } from 'components/Headings';
import { MediumUp, Small } from 'components/MediaQuery';
import Modal from 'components/Modal';
import { mediumUp } from 'constants/mediaQueries';
import FreeShippingIndicator from 'containers/FreeShippingIndicatorContainer';
import PdpAddToCartModalRecommendations from 'containers/PdpAddToCartModalRecommendationsContainer';
import CartUpsell from 'models/CartUpsell';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

import { LOADING_STATE, SIZES, CLOSE_MODAL_TYPES, CLOSE_MODAL_LOCATIONS } from './constants';
import ProductCard from './ProductCard';
import Subtotal from './Subtotal';

const AddToCartModal = ({
  isOpen,
  itemCount,
  closeModal,
  product,
  openCart,
  subtotalWithCurrency,
  currentVariant,
  recommendationsOption,
}) => {
  const isMediumUp = useMediaQuery({ query: mediumUp });

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() =>
        closeModal({
          closeType: CLOSE_MODAL_TYPES.CLICK,
          closeLocation: CLOSE_MODAL_LOCATIONS.EXIT_MODAL,
        })
      }
      className="AddToCartModal"
      portalClassName="AddToCartModal__portal"
    >
      <div className="AddToCartModal__cart-content">
        <div className="AddToCartModal__heading">
          <Small>
            <div className="AddToCartModal__heading-cart-icon">
              <CartCount itemCount={itemCount} />
            </div>
          </Small>
          <div className="AddToCartModal__heading-label">
            <TertiaryHeading>{I18n.localize('cart.greatTaste')}</TertiaryHeading>
          </div>
          <MediumUp>
            <div className="AddToCartModal__heading-cart-icon">
              <CartCount itemCount={itemCount} />
            </div>
          </MediumUp>
          <div className="AddToCartModal__heading-free-shipping">
            <FreeShippingIndicator showProgressBar={false} largeText showPeaceIcon={false} />
          </div>
        </div>
        <div className="AddToCartModal__product-cart-container">
          <div className="AddToCartModal__product">
            <ProductCard
              product={product}
              currentVariant={currentVariant}
              imageSize={isMediumUp ? SIZES.PRODUCT_CARD_LARGE : SIZES.PRODUCT_CARD_SMALL}
            />
          </div>
          <div className="AddToCartModal__subtotal">
            <Subtotal
              subtotal={subtotalWithCurrency}
              smallButtons={!isMediumUp}
              openCart={openCart}
              closeModal={closeModal}
            />
          </div>
        </div>
      </div>
      <PdpAddToCartModalRecommendations
        product={product}
        recommendationsOption={recommendationsOption}
      />
      <style jsx global>{`
        .AddToCartModal__portal .Modal__content {
          padding: ${Theme.get('spacing') * 3}px;
        }

        .AddToCartModal__heading-free-shipping .FreeShippingIndicator__messaging-container {
          text-align: left;
        }

        .AddToCartModal__cart-content {
          margin-bottom: ${Theme.get('spacing') * 3}px;
        }

        .AddToCartModal__heading {
          border-bottom: solid 1px ${Theme.get('colors-black-20')};
          margin-bottom: ${Theme.get('spacing') * 2.5}px;
        }

        .AddToCartModal__heading-cart-icon,
        .AddToCartModal__heading-label,
        .AddToCartModal__heading-free-shipping {
          margin-bottom: ${Theme.get('spacing')}px;
        }

        .AddToCartModal__heading-free-shipping {
          clear: both;
        }

        .AddToCartModal__product {
          margin-bottom: ${Theme.get('spacing') * 2.5}px;
        }

        .AddToCartModal__recommendation-loading {
          width: ${LOADING_STATE.WIDTH_MOBILE}px;
          height: ${LOADING_STATE.HEIGHT}px;
        }

        .AddToCartModal__recommendation-loading .LoadingState {
          inset: ${LOADING_STATE.INSET_MOBILE}px 0px 0px 0px;
        }

        .AddToCartModal__recommendations-top {
          display: inline-block;
          margin-bottom: ${Theme.get('spacing') * 2.5}px;
          width: 100%;
        }

        .AddToCartModal__recommendations-top .typography--tertiary-heading {
          font-size: ${Theme.get('font-size-large-px')};
        }

        .AddToCartModal__recommendations-product {
          margin: 0 ${Theme.get('spacing')}px;
        }

        .AddToCartModal__recommendations-product .Colorway .Sub {
          font-size: ${Theme.get('font-size-small-px')};
          text-wrap: wrap;
        }

        .AddToCartModal__recommendation-content .Carousel {
          margin: 0 -${Theme.get('spacing')}px;
        }

        @media ${mediumUp} {
          .AddToCartModal__portal .AddToCartModal.Modal__content {
            width: ${SIZES.MODAL_WIDTH_DESKTOP}px;
            min-height: ${SIZES.MIN_MODAL_HEIGHT_DESKTOP}px;
            max-height: ${SIZES.MAX_MODAL_HEIGHT_DESKTOP}px;
          }

          .AddToCartModal__heading-cart-icon {
            padding: ${Theme.get('spacing')}px 0 0 ${Theme.get('spacing') * 2}px;
          }

          .AddToCartModal__heading-cart-icon,
          .AddToCartModal__heading-label {
            float: left;
          }

          .AddToCartModal__product-cart-container {
            display: flex;
          }

          .AddToCartModal__product {
            flex: 1;
          }

          .AddToCartModal__subtotal {
            flex: 0 0 40%;
          }

          .AddToCartModal .Carousel > div:first-of-type {
            width: 100% !important;
          }

          .AddToCartModal__recommendation-loading {
            width: ${SIZES.MODAL_WIDTH_DESKTOP}px;
            height: ${LOADING_STATE.HEIGHT}px;
          }

          .AddToCartModal__recommendation-loading .LoadingState {
            inset: ${LOADING_STATE.INSET_DESKTOP}px 0px 0px 0px;
          }
        }
      `}</style>
    </Modal>
  );
};

AddToCartModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  product: PropTypes.shape({}).isRequired,
  currentVariant: PropTypes.shape({}),
  isOpen: PropTypes.bool.isRequired,
  openCart: PropTypes.func.isRequired,
  recommendationsOption: PropTypes.string.isRequired,
  itemCount: PropTypes.number,
  subtotalWithCurrency: PropTypes.string,
  recommendations: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.instanceOf(CartUpsell), PropTypes.shape({})])
  ),
};

AddToCartModal.defaultProps = {
  recommendations: [],
  itemCount: null,
  currentVariant: null,
  subtotalWithCurrency: null,
};

export default AddToCartModal;
